import React from 'react';
import cx from 'classnames';
import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import * as style from './Badge.css';
import { IconType, Icons } from '../../icons';

export type BadgeVariantTypes = NonNullable<style.BadgeVariants>;

export type BadgeProps = React.HTMLAttributes<HTMLElement> &
  style.BadgeVariants & {
    disabled?: boolean;
    iconLeft?: IconType;
    iconRight?: IconType;
    iconOnly?: boolean;
    asLink?: boolean;
    href?: string;
    /** When true, adds a variant-appropriate border to the badge */
    bordered?: boolean;
  };

export type IconBadgeProps = Omit<BadgeProps, 'iconLeft' | 'iconRight' | 'iconOnly'> & {
  label: string;
  icon: IconType;
  children?: never;
};

const spacingValue = (variant: BadgeProps['variant'], iconOnly?: BadgeProps['iconOnly']) => {
  if (variant === 'numerical') {
    return 'small';
  }
  return iconOnly ? 'extraSmall' : 'medium';
};

const buttonEventHandlers = {
  onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
};

export const Badge = React.forwardRef<HTMLButtonElement, BadgeProps>(
  (
    {
      variant,
      rounded,
      size,
      iconOnly = false,
      asButton,
      asLink,
      href,
      iconLeft,
      iconRight,
      disabled = false,
      children,
      capitalize,
      bordered,
      className,
      style: styleProp,
      ...props
    }: BadgeProps,
    forwardedRef,
  ) => {
    const sizeWithDefault = variant === 'numerical' ? 'extraSmall' : size;
    const LeadingIcon = !iconOnly && (iconLeft ?? (variant === 'recommended' && Icons.AiBolt));
    const TrailingIcon = !iconOnly && iconRight;
    const spacing = spacingValue(variant, iconOnly);
    const iconVariant = variant === 'recommended' ? 'recommended' : 'default';

    // Combine badge class with any additional className using cx
    const badgeClass = style.badge({
      variant,
      size: sizeWithDefault,
      spacing,
      asButton: asButton || Boolean(asLink && href),
      rounded,
      capitalize,
      bordered,
    });

    if (asLink && href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
          style={styleProp}
          className={cx(badgeClass, className)}
        >
          {LeadingIcon && <LeadingIcon className={style.badgeIcon[iconVariant]} />}
          {children}
          {TrailingIcon && <TrailingIcon className={style.badgeIcon.default} />}
        </a>
      );
    }

    if (asButton) {
      return (
        <button
          type="button"
          disabled={disabled}
          ref={forwardedRef}
          {...buttonEventHandlers}
          {...props}
          style={styleProp}
          className={cx(badgeClass, className)}
        >
          {LeadingIcon && <LeadingIcon className={style.badgeIcon[iconVariant]} />}
          {children}
          {TrailingIcon && <TrailingIcon className={style.badgeIcon.default} />}
        </button>
      );
    }

    return (
      <div {...props} style={styleProp} className={cx(badgeClass, className)}>
        {LeadingIcon && <LeadingIcon className={style.badgeIcon[iconVariant]} />}
        {children}
        {TrailingIcon && <TrailingIcon className={style.badgeIcon.default} />}
      </div>
    );
  },
);

export const IconBadge = React.forwardRef<HTMLButtonElement, IconBadgeProps>(
  ({ icon, variant, rounded, label, ...props }: IconBadgeProps, forwardedRef): JSX.Element => {
    const Icon = icon;
    return (
      <Badge {...props} ref={forwardedRef} iconOnly>
        <AccessibleIcon.Root label={label}>
          <Icon className={style.badgeIcon.default} />
        </AccessibleIcon.Root>
      </Badge>
    );
  },
);
